import { doc, getDoc, getDocs, setDoc, updateDoc, addDoc, collection, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";
import { noteFeedbackTitle } from "./notificationCopy";

/**
 * Get a user's personal reflection note for a piece of content.
 * contentType: "devotional" | "lesson" | "fruto"
 * Returns null if none exists yet.
 */
export async function getPersonalNote(userId, contentType, contentId) {
  const snap = await getDoc(doc(db, "personalNotes", `${userId}_${contentType}_${contentId}`));
  return snap.exists() ? snap.data() : null;
}

/**
 * Create or update a user's personal note (note text, favorite flag, and/or the
 * explicit mentor-sharing toggle). Never sets sharedWithMentor on its own — only
 * whatever the caller passes in updates.
 */
export async function savePersonalNote(userId, contentType, contentId, updates, language = "es") {
  const noteId = `${userId}_${contentType}_${contentId}`;
  await setDoc(
    doc(db, "personalNotes", noteId),
    {
      userId,
      contentType,
      contentId,
      ...updates,
      language,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Save or update mentor feedback on a specific personal note.
 * Mirrors study-service.js:saveMentorFeedback — only allowed by firestore.rules
 * when the note's sharedWithMentor flag is true and the caller is the assigned mentor.
 * S14b: avisa al discípulo cuando su mentor responde una nota compartida — la mitad
 * que faltaba de note_shared (esa dirección ya notifica; esta no lo hacía).
 */
export async function saveNoteMentorFeedback(noteDocId, feedbackText, studentId, language) {
  await updateDoc(doc(db, "personalNotes", noteDocId), {
    mentorFeedback: {
      text: feedbackText,
      at: serverTimestamp(),
    },
  });

  if (studentId) {
    const lang = language === "en" ? "en" : "es";
    await addDoc(collection(db, "notifications"), {
      userId: studentId,
      type: "note_feedback",
      language: lang,
      payload: { title: noteFeedbackTitle(lang), body: feedbackText },
      status: "pending",
      readAt: null,
      createdAt: serverTimestamp(),
    });
  }
}

/**
 * Mark a personal note's mentor feedback as seen by the student who owns it.
 * Mirrors study-service.js:markResponseFeedbackSeen.
 */
export async function markNoteFeedbackSeen(noteDocId) {
  await updateDoc(doc(db, "personalNotes", noteDocId), {
    "mentorFeedback.seenByStudent": true,
  });
}

/**
 * Get all personal notes a student explicitly shared with their mentor.
 * Both filters are required together (not just userId, filtered client-side) —
 * firestore.rules only allows a mentor to read notes with sharedWithMentor:true, and
 * a list query must match that exactly or Firestore rejects the whole query rather
 * than silently omitting the private notes.
 */
export async function getStudentPersonalNotes(studentId) {
  const q = query(
    collection(db, "personalNotes"),
    where("userId", "==", studentId),
    where("sharedWithMentor", "==", true)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Get ALL of a user's own personal notes (private and shared) — for their own
 * "Mis notas y favoritos" view. Owner-only, no sharedWithMentor filter needed since
 * firestore.rules already allows the owner to read every field of their own notes.
 */
export async function getMyPersonalNotes(userId) {
  const q = query(collection(db, "personalNotes"), where("userId", "==", userId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
